import {
  Action,
  Alert,
  Form,
  FormControl,
  FormSubmitHandler,
  NavigateWithoutBlocker,
  showNotification,
} from 'platform/components';
import {HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CreatePurchasePriceRuleApiResponse,
  PurchasePriceRuleRequestBody,
  PurchasePriceRuleResponseBody,
  UpdatePurchasePriceRuleApiResponse,
} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';
import {i18n} from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, buildArray, useNavigate} from 'shared/web';

import {PurchasePriceRuleTemplate} from '../../types/PurchasePriceRuleTemplateSchema';
import {PurchasePriceRuleBandsSection} from './components/PurchasePriceRuleBandsSection';
import {PurchasePriceRuleModifiersSection} from './components/PurchasePriceRuleModifiersSection';
import {PurchasePriceRuleNameField} from './components/PurchasePriceRuleNameField';
import {PurchasePriceRuleScopeSection} from './components/PurchasePriceRuleScopeSection';
import {PurchasePriceRuleWarrantySection} from './components/PurchasePriceRuleWarrantySection';
import {
  PurchasePriceRuleFormSchema,
  PurchasePriceRuleFormValues,
  SystemPurchasePriceRuleFormSchema,
} from './PurchasePriceRuleFormSchema';
import {formValuesToRequestBody} from './utils/formValuesToRequestBody';
import {responseToFormValues} from './utils/responseToFormValues';
import {templateToFormValues} from './utils/templateToFormValues';

interface PurchasePriceRuleFormProps {
  /** What the form starts from: an existing rule to edit, or a template for a new rule. */
  source:
    | {type: 'rule'; rule: PurchasePriceRuleResponseBody}
    | {type: 'template'; template: PurchasePriceRuleTemplate};
  isReadOnly: boolean;
  /** Saves the rule. The form reports the result and closes itself once the rule is saved. */
  onSave: (
    body: PurchasePriceRuleRequestBody
  ) => Promise<CreatePurchasePriceRuleApiResponse | UpdatePurchasePriceRuleApiResponse>;
  baseTestId: (suffix: string) => string;
}

export function PurchasePriceRuleForm(props: PurchasePriceRuleFormProps) {
  const navigate = useNavigate();
  const [saveError, setSaveError] = useState<ReturnType<typeof getApiErrorMessages> | null>(null);

  const defaultValues =
    props.source.type === 'rule'
      ? responseToFormValues(props.source.rule)
      : templateToFormValues(props.source.template);

  // The system rule is saved under its technical name, not the translated name shown in the form.
  const systemRuleName =
    props.source.type === 'rule' && props.source.rule.system ? props.source.rule.name : null;
  const isSystemRule = isNotNil(systemRuleName);

  const handleSubmit: FormSubmitHandler<PurchasePriceRuleFormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    setSaveError(null);

    await props
      .onSave(formValuesToRequestBody(values, systemRuleName))
      .then(() => {
        showNotification.success(
          i18n.t('page.settings.purchasePrice.ruleEditor.savedNotification')
        );

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }

        navigate(settingsRoutes.purchasePrice);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {
          silent: true,
          callback: ({error}) => setSaveError(getApiErrorMessages(error)),
          signalApiError: blockerContext?.reportApiFailure,
        })
      );
  };

  return (
    <SettingsSection>
      <Form<PurchasePriceRuleFormValues>
        experimentalZodSchema={
          isSystemRule ? SystemPurchasePriceRuleFormSchema : PurchasePriceRuleFormSchema
        }
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isDisabled={props.isReadOnly}
        shouldWatchForUnsavedChanges
        data-testid={props.baseTestId('form')}
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <PurchasePriceRuleFormContent
            control={control}
            formApi={formApi}
            navigateWithoutBlocker={navigateWithoutBlocker}
            isSystemRule={isSystemRule}
            isReadOnly={props.isReadOnly}
            saveError={saveError}
            baseTestId={props.baseTestId}
          />
        )}
      </Form>
    </SettingsSection>
  );
}

interface PurchasePriceRuleFormContentProps {
  control: FormControl<PurchasePriceRuleFormValues>;
  formApi: UseFormReturn<PurchasePriceRuleFormValues>;
  navigateWithoutBlocker: NavigateWithoutBlocker;
  isSystemRule: boolean;
  isReadOnly: boolean;
  saveError: ReturnType<typeof getApiErrorMessages> | null;
  baseTestId: (suffix: string) => string;
}

function PurchasePriceRuleFormContent(props: PurchasePriceRuleFormContentProps) {
  const [isNameEditedByUser, setIsNameEditedByUser] = useState(false);

  // A rule that starts without a name gets one suggested from its scope until the user edits it.
  const shouldSuggestName =
    !isNameEditedByUser && isNilOrEmpty(props.formApi.formState.defaultValues?.name);

  const handleClose = () => props.navigateWithoutBlocker(settingsRoutes.purchasePrice);

  const footerActions = buildArray<Action>()
    .when(props.isReadOnly, {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.close'),
      onClick: handleClose,
      'data-testid': props.baseTestId('close'),
    })
    .whenNot(props.isReadOnly, {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.discardChanges'),
      onClick: handleClose,
      'data-testid': props.baseTestId('discard'),
    })
    .whenNot(props.isReadOnly, {
      type: 'form-button',
      control: props.control,
      title: i18n.t('general.actions.saveAndClose'),
      buttonType: 'submit',
      'data-testid': props.baseTestId('submit'),
    });

  return (
    <VStack spacing={6}>
      <Show when={props.isSystemRule}>
        <HStack spacing={3} align="flex-start">
          {/* Centred on the first 20px line of the small text. */}
          <HStack height={5} align="center" shrink={0}>
            <Icon value="action/lock" size={4} color="palettes.neutral.60.100" />
          </HStack>
          <Text size="small" color="secondary" data-testid={props.baseTestId('systemRuleNote')}>
            {i18n.t('page.settings.purchasePrice.ruleEditor.systemRuleNote')}
          </Text>
        </HStack>
      </Show>

      <PurchasePriceRuleNameField
        control={props.control}
        isSystemRule={props.isSystemRule}
        onUserChange={() => setIsNameEditedByUser(true)}
        baseTestId={props.baseTestId}
      />

      <Show when={!props.isSystemRule}>
        <PurchasePriceRuleScopeSection
          control={props.control}
          shouldSuggestName={shouldSuggestName}
          formApi={props.formApi}
          baseTestId={props.baseTestId}
        />
      </Show>

      <PurchasePriceRuleBandsSection
        control={props.control}
        isReadOnly={props.isReadOnly}
        baseTestId={props.baseTestId}
      />

      <PurchasePriceRuleWarrantySection control={props.control} baseTestId={props.baseTestId} />

      <PurchasePriceRuleModifiersSection control={props.control} baseTestId={props.baseTestId} />

      <Show when={isNotNilOrEmpty(props.saveError) ? props.saveError : null}>
        {(saveError) => (
          <Alert
            type="inline"
            variant="error"
            message={saveError}
            data-testid={props.baseTestId('saveError')}
          />
        )}
      </Show>

      <SettingsFooter actions={footerActions} />
    </VStack>
  );
}
